const SMA = (rows, p) => rows.map((_, i) => i < p - 1 ? null : rows.slice(i - p + 1, i + 1).reduce((a, r) => a + r.close, 0) / p);
const EMA = (rows, p) => {
  const k = 2 / (p + 1), out = [];
  rows.forEach((r, i) => out.push(i === 0 ? r.close : r.close * k + out[i - 1] * (1 - k)));
  return out;
};
const STD = (rows, p, i, mean) =>
  Math.sqrt(rows.slice(i - p + 1, i + 1).reduce((a, r) => a + (r.close - mean) ** 2, 0) / p);
const toPoints = (rows, vals) =>
  rows.map((r, i) => (vals[i] == null || !Number.isFinite(vals[i]) ? null : { time: r.time, value: vals[i] })).filter(Boolean);

function rsi(rows, p = 14) {
  let g = 0, l = 0; const out = [null];
  for (let i = 1; i < rows.length; i++) {
    const d = rows[i].close - rows[i - 1].close;
    const up = Math.max(d, 0), dn = Math.max(-d, 0);
    if (i <= p) { g += up / p; l += dn / p; out.push(i < p ? null : 100 - 100 / (1 + (l === 0 ? 100 : g / l))); }
    else { g = (g * (p - 1) + up) / p; l = (l * (p - 1) + dn) / p; out.push(100 - 100 / (1 + (l === 0 ? 100 : g / l))); }
  }
  return out;
}
function macd(rows) {
  const fast = EMA(rows, 12), slow = EMA(rows, 26);
  const line = rows.map((_, i) => fast[i] - slow[i]);
  const sigRows = line.map((v, i) => ({ close: v, i }));
  const sig = EMA(sigRows, 9);
  return { line, signal: sig, hist: rows.map((_, i) => line[i] - sig[i]) };
}
function bollinger(rows, p = 20, mult = 2) {
  const mid = SMA(rows, p);
  return {
    mid,
    upper: mid.map((m, i) => (m == null ? null : m + mult * STD(rows, p, i, m))),
    lower: mid.map((m, i) => (m == null ? null : m - mult * STD(rows, p, i, m))),
  };
}
function vwap(rows) {
  // Session-anchored: resets at 00:00 UTC
  let pv = 0, vv = 0, day = null;
  return rows.map((r) => {
    const d = new Date(r.time * 1000).getUTCDate();
    if (d !== day) { pv = 0; vv = 0; day = d; }
    const v = Math.max(1, r.volume || 1);
    pv += ((r.high + r.low + r.close) / 3) * v; vv += v;
    return pv / vv;
  });
}
function pivotsDaily(rows) {
  // Classic daily pivots from previous UTC day, drawn forward as step lines
  const days = new Map();
  rows.forEach((r) => {
    const key = new Date(r.time * 1000).toISOString().slice(0, 10);
    if (!days.has(key)) days.set(key, { h: -Infinity, l: Infinity, c: r.close });
    const d = days.get(key);
    d.h = Math.max(d.h, r.high); d.l = Math.min(d.l, r.low); d.c = r.close;
  });
  const keys = [...days.keys()];
  const out = { P: [], R1: [], S1: [] };
  rows.forEach((r) => {
    const key = new Date(r.time * 1000).toISOString().slice(0, 10);
    const prev = keys[keys.indexOf(key) - 1];
    if (!prev) return;
    const d = days.get(prev), P = (d.h + d.l + d.c) / 3;
    out.P.push({ time: r.time, value: P });
    out.R1.push({ time: r.time, value: 2 * P - d.l });
    out.S1.push({ time: r.time, value: 2 * P - d.h });
  });
  return out;
}

export const INDICATORS = [
  { key: "sma20", label: "SMA 20", pane: "price" },
  { key: "sma50", label: "SMA 50", pane: "price" },
  { key: "sma200", label: "SMA 200", pane: "price" },
  { key: "ema20", label: "EMA 20", pane: "price" },
  { key: "ema50", label: "EMA 50", pane: "price" },
  { key: "bollinger", label: "Bollinger (20,2)", pane: "price" },
  { key: "vwap", label: "VWAP", pane: "price" },
  { key: "pivots", label: "Pivots (D)", pane: "price" },
  { key: "rsi", label: "RSI 14", pane: "lower" },
  { key: "macd", label: "MACD", pane: "lower" },
];

export function computeIndicator(key, rows) {
  switch (key) {
    case "sma20": return [{ kind: "line", color: "#3867e8", data: toPoints(rows, SMA(rows, 20)) }];
    case "sma50": return [{ kind: "line", color: "#9333ea", data: toPoints(rows, SMA(rows, 50)) }];
    case "sma200": return [{ kind: "line", color: "#6b7280", data: toPoints(rows, SMA(rows, 200)) }];
    case "ema20": return [{ kind: "line", color: "#0891b2", data: toPoints(rows, EMA(rows, 20)) }];
    case "ema50": return [{ kind: "line", color: "#d97706", data: toPoints(rows, EMA(rows, 50)) }];
    case "bollinger": {
      const b = bollinger(rows);
      return [
        { kind: "line", color: "#94a3b8", data: toPoints(rows, b.upper) },
        { kind: "line", color: "#64748b", data: toPoints(rows, b.mid) },
        { kind: "line", color: "#94a3b8", data: toPoints(rows, b.lower) },
      ];
    }
    case "vwap": return [{ kind: "line", color: "#db2777", data: toPoints(rows, vwap(rows)) }];
    case "pivots": {
      const p = pivotsDaily(rows);
      return [
        { kind: "line", color: "#1f2937", data: p.P },
        { kind: "line", color: "#059669", data: p.R1 },
        { kind: "line", color: "#dc2626", data: p.S1 },
      ];
    }
    case "rsi": return [{ kind: "line", color: "#7c3aed", data: toPoints(rows, rsi(rows)), priceScaleId: "rsi" }];
    case "macd": {
      const m = macd(rows);
      return [
        { kind: "line", color: "#3867e8", data: toPoints(rows, m.line), priceScaleId: "macd" },
        { kind: "line", color: "#d97706", data: toPoints(rows, m.signal), priceScaleId: "macd" },
        { kind: "histogram", data: toPoints(rows, m.hist), priceScaleId: "macd" },
      ];
    }
    default: return [];
  }
}
